import * as React from 'react';
import {Mutation} from 'react-apollo';

import gql from 'graphql-tag';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {GraphQLErrorObject} from 'nutshell-core/utils/graphql-errors';

import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {
    DuplicateStageset as DuplicateStagesetMutation,
    DuplicateStagesetVariables as DuplicateStagesetMutationVariables,
    DuplicateStagesetInputFieldInput,
    StagesetsForPipelineList as StagesetsForPipelineListQuery,
} from 'nutshell-graphql-types';

import STAGESET_BASIC from '../graphql/fragments/stageset-basic.graphql';
import GET_STAGESETS from '../graphql/queries/get-stagesets-for-pipeline-list.graphql';

import styles from './pipeline-name-popup.module.css';

type OwnProps = {
    onClose: (e?: React.SyntheticEvent<any>) => any;
    onSuccess: (e?: React.SyntheticEvent<any>) => any;
    stageset: {
        id: string;
        name: string;
    };
};

type State = {
    isSaving: boolean;
    errorMessage?: string;
    pipelineName?: string;
};

type GraphQLProps = {
    duplicateStageset: (arg1: DuplicateStagesetInputFieldInput) => Promise<any>;
};
type Props = OwnProps & GraphQLProps;

export class PipelineDuplicatePopupComponent extends React.PureComponent<Props, State> {
    nameTextFieldRef: HTMLInputElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isSaving: false,
            errorMessage: undefined,
            pipelineName: undefined,
        };
    }

    render() {
        return (
            // @ts-expect-error
            <form onSubmit={this.handleSubmit} className={styles['create-new-pipeline-form']}>
                <SingleInputModal
                    title='Make a copy'
                    subtitle='Name a copy of your pipeline'
                    errorMessage={this.state.errorMessage}
                    initialValue={`Copy of ${this.props.stageset.name}`}
                    buttonText={'Make a copy'}
                    onClose={this.props.onClose}
                    isSaving={this.state.isSaving}
                    onSave={this.handleSave}
                />
            </form>
        );
    }

    handleSave = (pipelineName: string) => {
        this.setState({pipelineName});
    };

    handleSubmit = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const newPipelineName =
            this.state.pipelineName && this.state.pipelineName.trim() !== ''
                ? this.state.pipelineName
                : 'Untitled pipeline';

        this.setState({isSaving: true});

        this.props
            .duplicateStageset({
                name: newPipelineName,
                id: this.props.stageset.id,
            })
            .then(() => {
                this.setState({
                    isSaving: false,
                });

                this.props.onSuccess();
                // Now we need to refetch the stagesets so the rest of the app knows
            })
            .then(() => Nut.managedCollections.Stagesets.fetch())
            .catch(this.handleError);
    };

    handleError = (err: GraphQLErrorObject) => {
        const message = safelyGetGraphQLErrorMessage(err);
        this.setState({
            errorMessage: message || undefined,
            isSaving: false,
        });
    };
}

// -----------------------------------------------------------------------------
//   DuplicateStageset mutation
// -----------------------------------------------------------------------------

const DUPLICATE_STAGESET = gql`
    mutation DuplicateStageset($stageset: DuplicateStagesetInputFieldInput!) {
        duplicateStageset(input: {stageset: $stageset}) {
            stageset {
                ...StagesetBasic
            }
        }
    }
    ${STAGESET_BASIC}
`;

class DuplicateMutation extends Mutation<
    DuplicateStagesetMutation,
    DuplicateStagesetMutationVariables
> {}

export const PipelineDuplicatePopup = (props: OwnProps) => (
    <DuplicateMutation
        mutation={DUPLICATE_STAGESET}
        update={(proxy, mutationResult) => {
            if (!mutationResult || !mutationResult.data) return;

            const duplicateStagesetPayload = mutationResult.data.duplicateStageset;

            // Read the data from our cache for this query.
            const cacheResult: StagesetsForPipelineListQuery | null | undefined = proxy.readQuery({
                query: GET_STAGESETS,
            });

            if (!cacheResult || !duplicateStagesetPayload) return;

            const position = duplicateStagesetPayload.stageset.position;

            // @ts-expect-error
            let newStagesets = [];

            // If position comes back as 1, we know the default pipeline was the one duplicated
            if (position === 1) {
                newStagesets = [duplicateStagesetPayload.stageset, ...cacheResult.stagesets];
            } else {
                let foundStageset = false;
                cacheResult.stagesets.forEach((stageset) => {
                    newStagesets.push(stageset);
                    if (stageset.position + 1 === position && !foundStageset) {
                        newStagesets.push(duplicateStagesetPayload.stageset);
                        foundStageset = true;
                    }
                });
            }

            // Write our data back to the cache.
            proxy.writeQuery({
                query: GET_STAGESETS,
                data: {
                    ...cacheResult,
                    // @ts-expect-error
                    stagesets: newStagesets,
                },
            });
        }}
    >
        {(duplicate) => (
            <PipelineDuplicatePopupComponent
                {...props}
                duplicateStageset={(payload) =>
                    duplicate({
                        variables: {
                            stageset: {
                                id: payload.id,
                                name: payload.name,
                            },
                        },
                    })
                }
            />
        )}
    </DuplicateMutation>
);
